import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {DEFAULT_DOCK_CONFIG, type DockConfig} from '../dock/types';
import {APPS, APP_BY_ID, DEFAULT_DOCK_ORDER} from '../data/apps';
import {useShellMode, type ShellMode} from '../shell/useShellMode';
import type {MotionOrigin, WindowState} from '../types';

export const MENU_BAR_HEIGHT = 36;
/** Room kept below a window for the dock, on the desktop shell. */
const DOCK_RESERVE = 110;

/**
 * Fixed, not user-tunable.
 *
 * `allowDragDelete` is off on purpose: this is a work tool, and an app that
 * disappears because a drag ended two pixels outside the dock is a support
 * ticket, not a feature. Magnification is also gentler than the showcase
 * template's 2x — it has to be pleasant forty times a day.
 */
const DOCK_CONFIG: DockConfig = {
  ...DEFAULT_DOCK_CONFIG,
  allowDragDelete: false,
  maxScale: 1.7,
  maxRange: 170,
};

interface DesktopValue {
  apps: typeof APPS;
  config: DockConfig;
  dockOrder: string[];
  windows: WindowState[];
  activeAppName: string;
  activeWindowId: string | null;
  shellMode: ShellMode;
  /** App id that should play its launch bounce right now. */
  launching: string | null;

  setDockOrder: (ids: string[]) => void;
  openApp: (appId: string) => void;
  goHome: () => void;
  closeWindow: (id: string) => void;
  focusWindow: (id: string) => void;
  minimizeWindow: (id: string) => void;
  /**
   * `origin` is the box the window should grow out of, measured by whoever was
   * clicked — the parking slot beside the dock, or the app icon. Passing it in
   * is the only accurate option: the slot unmounts in this very update, and a
   * magnified dock item is not where it was drawn a frame ago.
   */
  restoreWindow: (id: string, origin?: DOMRect) => void;
  /** Reads and clears that box. Called once, by the motion layer. */
  takeRestoreOrigin: (id: string) => MotionOrigin | null;
  /**
   * Windows whose parking slot must stay on screen even though they are no
   * longer minimised — they are still pouring back out of it.
   */
  heldSlots: string[];
  /** The motion layer says the window is fully out; the slot may go. */
  releaseSlot: (id: string) => void;
  toggleMaximize: (id: string) => void;
  moveWindow: (id: string, x: number, y: number) => void;
  resizeWindow: (id: string, width: number, height: number) => void;
}

const DesktopContext = createContext<DesktopValue | null>(null);

export function useDesktop(): DesktopValue {
  const value = useContext(DesktopContext);
  if (!value) throw new Error('useDesktop must be used inside <DesktopProvider>');
  return value;
}

export function DesktopProvider({children}: {children: ReactNode}) {
  const shellMode = useShellMode();
  const [dockOrder, setDockOrderState] = useState<string[]>(DEFAULT_DOCK_ORDER);
  const [windows, setWindows] = useState<WindowState[]>([]);
  const [launching, setLaunching] = useState<string | null>(null);
  const [heldSlots, setHeldSlots] = useState<string[]>([]);

  const zRef = useRef(10);
  const seqRef = useRef(0);
  const windowsRef = useRef(windows);
  windowsRef.current = windows;
  const shellModeRef = useRef(shellMode);
  shellModeRef.current = shellMode;
  const bounceTimerRef = useRef<number | null>(null);
  const restoreOriginsRef = useRef(new Map<string, MotionOrigin>());

  const setDockOrder = useCallback((ids: string[]) => {
    setDockOrderState((prev) => {
      const known = new Set(prev);
      const next = ids.filter((id) => known.has(id));
      if (next.length !== prev.length) return prev;
      return next.every((id, index) => id === prev[index]) ? prev : next;
    });
  }, []);

  const focusWindow = useCallback((id: string) => {
    zRef.current += 1;
    const z = zRef.current;
    setWindows((prev) => prev.map((win) => (win.id === id ? {...win, z} : win)));
  }, []);

  const releaseSlot = useCallback((id: string) => {
    // Bail when nothing changes: this is called on every commit of a window that
    // is not minimised, and a fresh array each time would loop forever.
    setHeldSlots((prev) => (prev.includes(id) ? prev.filter((held) => held !== id) : prev));
  }, []);

  const restoreWindow = useCallback(
    (id: string, origin?: DOMRect) => {
      // Always write, never merge: a leftover box from an earlier restore would
      // send the next flight to a slot that has since moved or gone.
      if (origin) {
        restoreOriginsRef.current.set(id, {
          height: origin.height,
          left: origin.left,
          top: origin.top,
          width: origin.width,
        });
      } else {
        restoreOriginsRef.current.delete(id);
      }

      // The slot has to outlive this update: the window pours back out of it,
      // and a slot that unmounted the moment it was clicked would leave the
      // animation growing out of a hole in the dock.
      const target = windowsRef.current.find((win) => win.id === id);
      if (target?.minimized) {
        setHeldSlots((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }

      setWindows((prev) => prev.map((win) => (win.id === id ? {...win, minimized: false} : win)));
      focusWindow(id);
    },
    [focusWindow],
  );

  const takeRestoreOrigin = useCallback((id: string) => {
    const origin = restoreOriginsRef.current.get(id) ?? null;
    restoreOriginsRef.current.delete(id);
    return origin;
  }, []);

  const playBounce = useCallback((appId: string) => {
    if (bounceTimerRef.current !== null) window.clearTimeout(bounceTimerRef.current);
    setLaunching(null);
    // A frame of `null` lets the same app bounce twice in a row.
    requestAnimationFrame(() => setLaunching(appId));
    bounceTimerRef.current = window.setTimeout(() => {
      setLaunching(null);
      bounceTimerRef.current = null;
    }, 1000);
  }, []);

  const openApp = useCallback(
    (appId: string) => {
      const app = APP_BY_ID[appId];
      if (!app) return;
      playBounce(appId);

      // One window per app: these are tools, not documents. Opening "Khách hàng"
      // twice would split the same list across two windows that can disagree.
      const existing = windowsRef.current.find((win) => win.appId === appId);
      if (existing) {
        restoreWindow(existing.id);
        return;
      }

      seqRef.current += 1;
      zRef.current += 1;
      const index = seqRef.current;
      const width = Math.min(app.window.width, window.innerWidth - 24);
      const height = Math.min(
        app.window.height,
        window.innerHeight - MENU_BAR_HEIGHT - DOCK_RESERVE,
      );
      const maxX = Math.max(12, window.innerWidth - width - 24);
      const maxY = Math.max(MENU_BAR_HEIGHT + 12, window.innerHeight - height - 132);

      setWindows((prev) => [
        ...prev,
        {
          appId,
          height,
          id: `win-${appId}`,
          maximized: false,
          minimized: false,
          restoreBox: null,
          width,
          x: Math.min(maxX, 96 + ((index * 34) % 200)),
          y: Math.min(maxY, MENU_BAR_HEIGHT + 32 + ((index * 26) % 120)),
          z: zRef.current,
        },
      ]);
    },
    [playBounce, restoreWindow],
  );

  const minimizeWindow = useCallback((id: string) => {
    setWindows((prev) => prev.map((win) => (win.id === id ? {...win, minimized: true} : win)));
  }, []);

  const goHome = useCallback(() => {
    if (shellModeRef.current !== 'mobile') return;
    setWindows((prev) =>
      prev.every((win) => win.minimized)
        ? prev
        : prev.map((win) => (win.minimized ? win : {...win, minimized: true})),
    );
  }, []);

  const closeWindow = useCallback(
    (id: string) => {
      setWindows((prev) => prev.filter((win) => win.id !== id));
      restoreOriginsRef.current.delete(id);
      releaseSlot(id);
    },
    [releaseSlot],
  );

  const moveWindow = useCallback((id: string, x: number, y: number) => {
    setWindows((prev) => prev.map((win) => (win.id === id ? {...win, x, y} : win)));
  }, []);

  const resizeWindow = useCallback((id: string, width: number, height: number) => {
    setWindows((prev) => prev.map((win) => (win.id === id ? {...win, height, width} : win)));
  }, []);

  const toggleMaximize = useCallback((id: string) => {
    setWindows((prev) =>
      prev.map((win) => {
        if (win.id !== id) return win;
        if (win.maximized && win.restoreBox) {
          return {...win, ...win.restoreBox, maximized: false, restoreBox: null};
        }
        return {
          ...win,
          height: window.innerHeight - MENU_BAR_HEIGHT - 132,
          maximized: true,
          restoreBox: {height: win.height, width: win.width, x: win.x, y: win.y},
          width: window.innerWidth - 24,
          x: 12,
          y: MENU_BAR_HEIGHT + 8,
        };
      }),
    );
  }, []);

  /**
   * Keep every window inside the viewport when it changes size, so the title bar
   * — the only drag handle a window has — can never end up off-screen.
   */
  useEffect(() => {
    if (shellMode === 'mobile') return;

    const onResize = () => {
      setWindows((prev) => {
        let changed = false;
        const next = prev.map((win) => {
          const width = Math.min(win.width, window.innerWidth - 24);
          const height = Math.min(
            win.height,
            window.innerHeight - MENU_BAR_HEIGHT - DOCK_RESERVE,
          );
          const x = Math.min(Math.max(win.x, 12 - width + 120), window.innerWidth - 120);
          const y = Math.min(
            Math.max(win.y, MENU_BAR_HEIGHT),
            Math.max(MENU_BAR_HEIGHT, window.innerHeight - 80),
          );
          if (width === win.width && height === win.height && x === win.x && y === win.y) {
            return win;
          }
          changed = true;
          return {...win, height, width, x, y};
        });
        return changed ? next : prev;
      });
    };

    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [shellMode]);

  const activeWindowId = useMemo(() => {
    const visible = windows.filter((win) => !win.minimized);
    if (visible.length === 0) return null;
    return visible.reduce((best, win) => (win.z > best.z ? win : best), visible[0]!).id;
  }, [windows]);

  const activeAppName = useMemo(() => {
    if (!activeWindowId) return '';
    const win = windows.find((entry) => entry.id === activeWindowId);
    return win ? (APP_BY_ID[win.appId]?.name ?? '') : '';
  }, [activeWindowId, windows]);

  const value = useMemo<DesktopValue>(
    () => ({
      activeAppName,
      activeWindowId,
      apps: APPS,
      closeWindow,
      config: DOCK_CONFIG,
      dockOrder,
      focusWindow,
      goHome,
      heldSlots,
      launching,
      minimizeWindow,
      moveWindow,
      openApp,
      releaseSlot,
      resizeWindow,
      restoreWindow,
      setDockOrder,
      shellMode,
      takeRestoreOrigin,
      toggleMaximize,
      windows,
    }),
    [
      activeAppName,
      activeWindowId,
      closeWindow,
      dockOrder,
      focusWindow,
      goHome,
      heldSlots,
      launching,
      minimizeWindow,
      moveWindow,
      openApp,
      releaseSlot,
      resizeWindow,
      restoreWindow,
      setDockOrder,
      shellMode,
      takeRestoreOrigin,
      toggleMaximize,
      windows,
    ],
  );

  return <DesktopContext.Provider value={value}>{children}</DesktopContext.Provider>;
}
